import { Search, Bell, MessageCircle, Settings, Menu } from 'lucide-react'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { SidebarTrigger } from '../ui/sidebar'
import { LogoIcon } from '../ui/logo'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu'
import { UserRole } from '../data/orderWorkflowData'
import { useNotificationsWithRealtime, useUnreadNotificationCount, useMarkNotificationAsRead } from '../../hooks/useNotifications'
import { formatDistanceToNow } from 'date-fns'
import { az } from 'date-fns/locale'
import { AdminNotificationCategory } from '@tenant/shared'
import { useAuth } from '../../contexts/AuthContext'

interface HeaderProps {
  userRole?: UserRole
  onRoleChange?: (role: UserRole) => void
}

const roleLabels: Record<UserRole, string> = {
  admin: 'Administrator',
  florist: 'Florist',
  courier: 'Kuryer',
}

export function Header({ userRole = 'admin' }: HeaderProps) {
  const { logout, user } = useAuth()

  // Fetch notifications with real-time updates
  const { data: notificationsData } = useNotificationsWithRealtime({ limit: 10, isRead: false })
  const { data: unreadCount } = useUnreadNotificationCount()
  const markAsRead = useMarkNotificationAsRead()

  const notifications = notificationsData?.notifications || []

  // Category color mapping
  const getCategoryColor = (category: AdminNotificationCategory) => {
    switch (category) {
      case AdminNotificationCategory.ORDERS:
        return 'bg-admin-accent'
      case AdminNotificationCategory.CHATS:
        return 'bg-blue-500'
      case AdminNotificationCategory.INVENTORY:
        return 'bg-yellow-500'
      case AdminNotificationCategory.PROCESSES:
        return 'bg-purple-500'
      case AdminNotificationCategory.SYSTEM:
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  const handleNotificationClick = (notificationId: string) => {
    markAsRead.mutate(notificationId)
  }

  return (
    <header className="bg-white shadow-sm px-6 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <SidebarTrigger className="md:hidden">
              <Menu className="w-5 h-5" />
            </SidebarTrigger>

            {/* Mobile Logo */}
            <div className="md:hidden">
              <LogoIcon size="sm" />
            </div>
          </div>

          <div className="relative w-64 max-w-sm hidden md:block">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Sifarişləri, müştəriləri axtar..."
              className="bg-white border-gray-300"
              style={{ paddingLeft: '2.5rem' }}
            />
          </div>

          {/* Current Role Indicator */}
          <Badge
            variant="outline"
            className="hidden sm:flex"
            style={{ backgroundColor: 'var(--color-brand-500)', color: '#fff', borderColor: 'var(--color-brand-500)' }}
          >
            {roleLabels[userRole]}
          </Badge>
        </div>

        <div className="flex items-center gap-3">
          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="relative">
                <Bell className="w-5 h-5" />
                {(unreadCount ?? 0) > 0 && (
                  <Badge className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs bg-admin-accent text-white">
                    {unreadCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="p-4 border-b">
                <h4 className="font-medium">{roleLabels[userRole]} üçün bildirişlər</h4>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    Yeni bildiriş yoxdur
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <DropdownMenuItem
                      key={notification.id}
                      className="p-4 flex flex-col items-start cursor-pointer"
                      onClick={() => handleNotificationClick(notification.id)}
                    >
                      <div className="flex items-center gap-2 w-full">
                        <div className={`w-2 h-2 ${getCategoryColor(notification.category)} rounded-full`}></div>
                        <span className="font-medium text-sm">{notification.title}</span>
                        <span className="text-xs text-gray-500 ml-auto">
                          {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true,
                            locale: az,
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                    </DropdownMenuItem>
                  ))
                )}
              </div>
              {notifications.length > 0 && (
                <DropdownMenuSeparator />
              )}
              {notifications.length > 0 && (
                <DropdownMenuItem className="p-2 text-center justify-center text-sm text-delivery">
                  Bütün bildirişlərə bax
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Messages */}
          <Button variant="ghost" size="sm" className="relative hidden sm:flex">
            <MessageCircle className="w-5 h-5" />
            <Badge className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs bg-admin-accent text-white">
              3
            </Badge>
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 p-2">
                <Avatar className="w-8 h-8">
                  <AvatarImage src="/avatar.jpg" />
                  <AvatarFallback className="bg-delivery text-white">
                    {(roleLabels[userRole] ?? userRole ?? '?')[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium">{user?.name || roleLabels[userRole] || 'Unknown'}</p>
                  <p className="text-xs text-gray-500">{user?.email || roleLabels[userRole]}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <Settings className="w-4 h-4 mr-2" />
                Parametrlər
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600" onClick={logout}>Çıxış</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}

